import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Settings,
  Trash2,
  AlertTriangle,
  Database,
  Eye,
  Rocket,
  RefreshCw,
  Check,
  Plus,
  FileText,
  Building2,
  Briefcase,
  Users,
  FilePlus2,
  ChevronRight,
  CheckCircle
} from 'lucide-react';

// Componente para configurar tipos de documento de empresas
const TiposDocumentoConfig = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [newTipo, setNewTipo] = useState({ codigo: '', nombre: '', obligatorio: true, orden: 99 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    try {
      const res = await api.get('/empresas/config/tipos-documento');
      setTipos(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddTipo = async () => {
    if (!newTipo.codigo.trim() || !newTipo.nombre.trim()) {
      toast.error('Código y nombre son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/empresas/config/tipos-documento', newTipo);
      toast.success('Tipo de documento agregado');
      setNewTipo({ codigo: '', nombre: '', obligatorio: true, orden: 99 });
      setShowAddForm(false);
      loadTipos();
    } catch (error) {
      toast.error('Error al agregar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTipo = async (tipoId, updates) => {
    try {
      await api.put(`/empresas/config/tipos-documento/${tipoId}`, updates);
      toast.success('Tipo de documento actualizado');
      loadTipos();
      setEditingTipo(null);
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleToggleObligatorio = async (tipo) => {
    await handleUpdateTipo(tipo.id, { obligatorio: !tipo.obligatorio });
  };

  const handleDeleteTipo = async (tipoId) => {
    if (!window.confirm('¿Eliminar este tipo de documento?')) return;
    try {
      await api.delete(`/empresas/config/tipos-documento/${tipoId}`);
      toast.success('Tipo eliminado');
      loadTipos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingTipo.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    await handleUpdateTipo(editingTipo.id, {
      nombre: editingTipo.nombre,
      obligatorio: editingTipo.obligatorio
    });
  };

  if (loading) {
    return <div className="text-center py-4"><RefreshCw className="animate-spin mx-auto" size={20} /></div>;
  }

  // Separar tipos obligatorios y opcionales
  const tiposObligatorios = tipos.filter(t => t.obligatorio);
  const tiposOpcionales = tipos.filter(t => !t.obligatorio);

  return (
    <div className="space-y-6">
      {/* Tipos Obligatorios */}
      <div>
        <h4 className="text-sm font-semibold mb-2" style={{ color: '#1e293b' }}>
          Documentos Obligatorios ({tiposObligatorios.length})
        </h4>
        <div className="space-y-2">
          {tiposObligatorios.map(tipo => (
            <div key={tipo.id} className="flex items-center justify-between p-3 rounded-lg" 
                 style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
              {editingTipo?.id === tipo.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={editingTipo.nombre}
                    onChange={(e) => setEditingTipo({ ...editingTipo, nombre: e.target.value })}
                    className="text-sm h-8"
                    style={{ backgroundColor: '#fff' }}
                  />
                  <button
                    onClick={handleSaveEdit}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingTipo(null)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <FileText size={16} style={{ color: '#d97706' }} />
                    <div>
                      <p className="font-medium text-sm" style={{ color: '#1e293b' }}>{tipo.nombre}</p>
                      <p className="text-xs" style={{ color: '#92400e' }}>
                        Código: {tipo.codigo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingTipo({ ...tipo })}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer'
                      }}
                      title="Editar"
                    >
                      ✎
                    </button>
                    <button
                      onClick={() => handleToggleObligatorio(tipo)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                      title="Cambiar a Opcional"
                    >
                      → Opcional
                    </button>
                    <button
                      onClick={() => handleDeleteTipo(tipo.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {tiposObligatorios.length === 0 && (
            <p className="text-sm text-center py-2" style={{ color: '#9ca3af' }}>
              No hay documentos obligatorios
            </p>
          )}
        </div>
      </div>

      {/* Tipos Opcionales */}
      <div>
        <h4 className="text-sm font-semibold mb-2" style={{ color: '#1e293b' }}>
          Documentos Opcionales ({tiposOpcionales.length})
        </h4>
        <div className="space-y-2">
          {tiposOpcionales.map(tipo => (
            <div key={tipo.id} className="flex items-center justify-between p-3 rounded-lg" 
                 style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
              {editingTipo?.id === tipo.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={editingTipo.nombre}
                    onChange={(e) => setEditingTipo({ ...editingTipo, nombre: e.target.value })}
                    className="text-sm h-8"
                    style={{ backgroundColor: '#fff' }}
                  />
                  <button
                    onClick={handleSaveEdit}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingTipo(null)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <FileText size={16} style={{ color: '#64748b' }} />
                    <div>
                      <p className="font-medium text-sm" style={{ color: '#1e293b' }}>{tipo.nombre}</p>
                      <p className="text-xs" style={{ color: '#64748b' }}>
                        Código: {tipo.codigo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingTipo({ ...tipo })}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer'
                      }}
                      title="Editar"
                    >
                      ✎
                    </button>
                    <button
                      onClick={() => handleToggleObligatorio(tipo)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '4px',
                        color: '#92400e',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                      title="Cambiar a Obligatorio"
                    >
                      → Obligatorio
                    </button>
                    <button
                      onClick={() => handleDeleteTipo(tipo.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {tiposOpcionales.length === 0 && (
            <p className="text-sm text-center py-2" style={{ color: '#9ca3af' }}>
              No hay documentos opcionales
            </p>
          )}
        </div>
      </div>
      
      {/* Formulario para agregar nuevo tipo */}
      {showAddForm ? (
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: '#166534' }}>Nuevo Tipo de Documento</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs">Código *</Label>
              <Input
                value={newTipo.codigo}
                onChange={(e) => setNewTipo({ ...newTipo, codigo: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                placeholder="Ej: DNI_REPRESENTANTE"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input
                value={newTipo.nombre}
                onChange={(e) => setNewTipo({ ...newTipo, nombre: e.target.value })}
                placeholder="Ej: DNI del Representante"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="obligatorio"
                checked={newTipo.obligatorio}
                onChange={() => setNewTipo({ ...newTipo, obligatorio: true })}
              />
              <span style={{ color: '#92400e', fontWeight: newTipo.obligatorio ? '600' : '400' }}>Obligatorio</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="obligatorio"
                checked={!newTipo.obligatorio}
                onChange={() => setNewTipo({ ...newTipo, obligatorio: false })}
              />
              <span style={{ color: '#64748b', fontWeight: !newTipo.obligatorio ? '600' : '400' }}>Opcional</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewTipo({ codigo: '', nombre: '', obligatorio: true, orden: 99 });
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleAddTipo}
              disabled={saving}
              style={{
                padding: '6px 12px',
                backgroundColor: '#7c3aed',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {saving ? 'Guardando...' : 'Agregar'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 12px',
            backgroundColor: '#fff',
            border: '1px dashed #7c3aed',
            borderRadius: '6px',
            color: '#7c3aed',
            fontSize: '14px',
            cursor: 'pointer',
            width: '100%',
            justifyContent: 'center',
            fontWeight: '500'
          }}
        >
          <Plus size={16} />
          Agregar Tipo de Documento
        </button>
      )}
    </div>
  );
};

// Componente para configurar tipos de caso
const TiposCasoConfig = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [newTipo, setNewTipo] = useState({ codigo: '', nombre: '', color: '#64748b', icono: 'Tag' });
  const [saving, setSaving] = useState(false);

  const ICONOS = [
    { value: 'ShieldAlert', label: 'Escudo' },
    { value: 'Headphones', label: 'Soporte' },
    { value: 'AlertTriangle', label: 'Alerta' },
    { value: 'Scale', label: 'Compliance' },
    { value: 'Tag', label: 'Etiqueta' },
    { value: 'Briefcase', label: 'Caso' },
  ];

  const COLORES = [
    { value: '#ef4444', label: 'Rojo' },
    { value: '#f97316', label: 'Naranja' },
    { value: '#eab308', label: 'Amarillo' },
    { value: '#22c55e', label: 'Verde' },
    { value: '#3b82f6', label: 'Azul' },
    { value: '#8b5cf6', label: 'Púrpura' },
    { value: '#64748b', label: 'Gris' },
  ];

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    try {
      const res = await api.get('/cases/config/tipos');
      setTipos(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddTipo = async () => {
    if (!newTipo.codigo.trim() || !newTipo.nombre.trim()) {
      toast.error('Código y nombre son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/cases/config/tipos', newTipo);
      toast.success('Tipo de caso agregado');
      setNewTipo({ codigo: '', nombre: '', color: '#64748b', icono: 'Tag' });
      setShowAddForm(false);
      loadTipos();
    } catch (error) {
      toast.error('Error al agregar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTipo = async (tipoId, updates) => {
    try {
      await api.put(`/cases/config/tipos/${tipoId}`, updates);
      toast.success('Tipo de caso actualizado');
      loadTipos();
      setEditingTipo(null);
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteTipo = async (tipoId) => {
    if (!window.confirm('¿Eliminar este tipo de caso?')) return;
    try {
      await api.delete(`/cases/config/tipos/${tipoId}`);
      toast.success('Tipo eliminado');
      loadTipos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return <div className="text-center py-4"><RefreshCw className="animate-spin mx-auto" size={20} /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {tipos.map(tipo => (
          <div key={tipo.id} className="flex items-center justify-between p-3 rounded-lg" 
               style={{ backgroundColor: `${tipo.color}15`, border: `1px solid ${tipo.color}40` }}>
            {editingTipo?.id === tipo.id ? (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  value={editingTipo.nombre}
                  onChange={(e) => setEditingTipo({ ...editingTipo, nombre: e.target.value })}
                  className="text-sm h-8 flex-1"
                  style={{ backgroundColor: '#fff' }}
                />
                <select
                  value={editingTipo.color}
                  onChange={(e) => setEditingTipo({ ...editingTipo, color: e.target.value })}
                  style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}
                >
                  {COLORES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => handleUpdateTipo(editingTipo.id, { nombre: editingTipo.nombre, color: editingTipo.color })}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={() => setEditingTipo(null)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: tipo.color }}
                  >
                    <span className="text-white text-xs">
                      {tipo.icono === 'ShieldAlert' ? '🛡️' : 
                       tipo.icono === 'Headphones' ? '🎧' :
                       tipo.icono === 'AlertTriangle' ? '⚠️' :
                       tipo.icono === 'Scale' ? '⚖️' : '🏷️'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#1e293b' }}>{tipo.nombre}</p>
                    <p className="text-xs" style={{ color: '#64748b' }}>Código: {tipo.codigo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingTipo({ ...tipo })}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer'
                    }}
                    title="Editar"
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => handleDeleteTipo(tipo.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#dc2626',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      
      {showAddForm ? (
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: '#166534' }}>Nuevo Tipo de Caso</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs">Código *</Label>
              <Input
                value={newTipo.codigo}
                onChange={(e) => setNewTipo({ ...newTipo, codigo: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                placeholder="Ej: RECLAMO"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input
                value={newTipo.nombre}
                onChange={(e) => setNewTipo({ ...newTipo, nombre: e.target.value })}
                placeholder="Ej: Reclamo"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <div>
              <Label className="text-xs">Color</Label>
              <select
                value={newTipo.color}
                onChange={(e) => setNewTipo({ ...newTipo, color: e.target.value })}
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: '4px', 
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  width: '100%',
                  marginTop: '4px'
                }}
              >
                {COLORES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Icono</Label>
              <select
                value={newTipo.icono}
                onChange={(e) => setNewTipo({ ...newTipo, icono: e.target.value })}
                style={{ 
                  padding: '6px 10px', 
                  borderRadius: '4px', 
                  fontSize: '13px',
                  border: '1px solid #d1d5db',
                  width: '100%',
                  marginTop: '4px'
                }}
              >
                {ICONOS.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewTipo({ codigo: '', nombre: '', color: '#64748b', icono: 'Tag' });
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleAddTipo}
              disabled={saving}
              style={{
                padding: '6px 12px',
                backgroundColor: '#7c3aed',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {saving ? 'Guardando...' : 'Agregar'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 12px',
            backgroundColor: '#fff',
            border: '1px dashed #7c3aed',
            borderRadius: '6px',
            color: '#7c3aed',
            fontSize: '14px',
            cursor: 'pointer',
            width: '100%',
            justifyContent: 'center',
            fontWeight: '500'
          }}
        >
          <Plus size={16} />
          Agregar Tipo de Caso
        </button>
      )}
    </div>
  );
};

// Custom Toggle Switch component with inline styles
const ToggleSwitch = ({ checked, onChange, disabled }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        backgroundColor: checked ? '#2563eb' : '#cbd5e1',
        borderRadius: '12px',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: 'none',
        padding: 0,
        transition: 'background-color 0.2s ease',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          width: '20px',
          height: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '50%',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked && <Check size={12} style={{ color: '#2563eb' }} />}
      </span>
    </button>
  );
};

const SystemSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    mostrar_credenciales_prueba: true,
    modo_produccion: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showClearAlertsDialog, setShowClearAlertsDialog] = useState(false);
  const [showClearCasesDialog, setShowClearCasesDialog] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [cleaningSections, setCleaningSections] = useState(false);
  const [browserStatus, setBrowserStatus] = useState(null);
  const [loadingBrowsers, setLoadingBrowsers] = useState(false);
  const [cleaningBrowsers, setCleaningBrowsers] = useState(false);

  useEffect(() => {
    loadSettings();
    loadBrowserStatus();
  }, []);

  const loadBrowserStatus = async () => {
    setLoadingBrowsers(true);
    try {
      const res = await api.get('/system/browser-status');
      setBrowserStatus(res.data);
    } catch (error) {
      // silencioso: solo admins pueden verlo
    } finally {
      setLoadingBrowsers(false);
    }
  };

  const handleCleanupBrowsers = async (force) => {
    if (force && !window.confirm('Esto cerrará TODOS los navegadores de automatización en curso. Úsalo solo si NO hay automatizaciones (ROS, Waya, UIF) corriendo. ¿Continuar?')) {
      return;
    }
    setCleaningBrowsers(true);
    try {
      const res = await api.post(`/system/cleanup-browsers?force=${force ? 'true' : 'false'}`);
      toast.success(res.data.message);
      await loadBrowserStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al limpiar procesos');
    } finally {
      setCleaningBrowsers(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get('/config/system');
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/config/system', settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAlerts = async () => {
    setClearingData(true);
    try {
      const res = await api.delete('/config/data/alerts');
      toast.success(res.data.message);
      setShowClearAlertsDialog(false);
    } catch (error) {
      toast.error('Error al eliminar alertas');
    } finally {
      setClearingData(false);
    }
  };

  const handleClearCases = async () => {
    setClearingData(true);
    try {
      const res = await api.delete('/config/data/cases');
      toast.success(res.data.message);
      setShowClearCasesDialog(false);
    } catch (error) {
      toast.error('Error al eliminar casos');
    } finally {
      setClearingData(false);
    }
  };

  // --- Tipos de resolución ---
  const [tiposResolucion, setTiposResolucion] = useState([]);
  const [newTipoRes, setNewTipoRes] = useState('');
  
  useEffect(() => {
    api.get('/cases/tipos-resolucion').then(r => setTiposResolucion(r.data)).catch(() => {});
  }, []);

  const addTipoResolucion = async () => {
    if (!newTipoRes.trim()) return;
    try {
      await api.post('/cases/tipos-resolucion', { nombre: newTipoRes.trim() });
      setNewTipoRes('');
      const r = await api.get('/cases/tipos-resolucion');
      setTiposResolucion(r.data);
      toast.success('Tipo de resolución agregado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const deleteTipoResolucion = async (nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      await api.delete(`/cases/tipos-resolucion/${encodeURIComponent(nombre)}`);
      const r = await api.get('/cases/tipos-resolucion');
      setTiposResolucion(r.data);
      toast.success('Eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleClearAll = async () => {
    setClearingData(true);
    try {
      const res = await api.delete('/config/data/all-test');
      toast.success(`Eliminados: ${res.data.alertas_eliminadas} alertas y ${res.data.casos_eliminados} casos`);
      setShowClearAllDialog(false);
    } catch (error) {
      toast.error('Error al eliminar datos');
    } finally {
      setClearingData(false);
    }
  };

  const handleCleanupSections = async () => {
    setCleaningSections(true);
    try {
      const res = await api.post('/modules/cleanup-sections');
      if (res.data.cleaned > 0) {
        toast.success(`Se eliminaron ${res.data.cleaned} secciones duplicadas. Recarga la página para ver los cambios.`);
      } else {
        toast.success('No se encontraron secciones duplicadas. El menú está limpio.');
      }
    } catch (error) {
      toast.error('Error al limpiar secciones duplicadas');
    } finally {
      setCleaningSections(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="text-blue-600" />
          Configuración del Sistema
        </h1>
        <p className="text-slate-600 mt-1">
          Opciones avanzadas y limpieza de datos
        </p>
      </div>

      <div className="grid gap-6 max-w-3xl">
        {/* Opciones de Visualización */}
        <Card className="p-6 bg-white">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Eye className="text-blue-600" size={20} />
            Opciones de Visualización
          </h2>
          
          <div className="space-y-4">
            <div 
              className="flex items-center justify-between py-3"
              style={{ borderBottom: '1px solid #e2e8f0' }}
            >
              <div>
                <Label className="text-sm font-medium" style={{ color: '#1e293b' }}>
                  Mostrar Credenciales de Prueba
                </Label>
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                  Muestra el cuadro con las credenciales de prueba en el login
                </p>
              </div>
              <ToggleSwitch
                checked={settings.mostrar_credenciales_prueba}
                onChange={(checked) => setSettings(s => ({ ...s, mostrar_credenciales_prueba: checked }))}
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <Label className="text-sm font-medium" style={{ color: '#1e293b' }}>
                  Modo Producción
                </Label>
                <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                  Activa el modo producción (desactiva funciones de prueba)
                </p>
              </div>
              <ToggleSwitch
                checked={settings.modo_produccion}
                onChange={(checked) => setSettings(s => ({ ...s, modo_produccion: checked }))}
              />
            </div>
          </div>

          <div className="mt-6 pt-4" style={{ borderTop: '1px solid #e2e8f0' }}>
            <button 
              onClick={saveSettings} 
              disabled={saving}
              data-testid="save-settings-btn"
              className="save-config-btn"
            >
              <style>{`
                .save-config-btn {
                  display: flex !important;
                  align-items: center !important;
                  gap: 8px !important;
                  padding: 10px 20px !important;
                  background-color: #2563eb !important;
                  border: none !important;
                  border-radius: 6px !important;
                  color: #ffffff !important;
                  font-size: 14px !important;
                  font-weight: 500 !important;
                  cursor: pointer !important;
                }
                .save-config-btn:hover {
                  background-color: #1d4ed8 !important;
                }
                .save-config-btn:disabled {
                  background-color: #93c5fd !important;
                  cursor: not-allowed !important;
                }
              `}</style>
              {saving ? (
                <>
                  <RefreshCw className="animate-spin" size={16} />
                  Guardando...
                </>
              ) : (
                'Guardar Configuración'
              )}
            </button>
          </div>
        </Card>

        {/* Tipos de Resolución */}
        <Card className="p-6 bg-white">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <CheckCircle className="text-green-600" size={20} />
            Tipos de Resolución de Casos
          </h2>
          <p className="text-xs text-slate-500 mb-3">Tipos disponibles al cerrar un caso (individual o masivo)</p>
          <div className="space-y-1.5 mb-3">
            {tiposResolucion.map(t => (
              <div key={t} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg group">
                <span className="text-sm text-slate-700">{t}</span>
                <button onClick={() => deleteTipoResolucion(t)}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs px-2">
                  Eliminar
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newTipoRes} onChange={e => setNewTipoRes(e.target.value)}
              placeholder="Nuevo tipo de resolución..."
              onKeyPress={e => e.key === 'Enter' && addTipoResolucion()}
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="new-tipo-res-input" />
            <Button onClick={addTipoResolucion} disabled={!newTipoRes.trim()} data-testid="add-tipo-res-btn">
              Agregar
            </Button>
          </div>
        </Card>

        {/* Mantenimiento del Sistema */}
        <Card className="p-6 bg-white">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Database className="text-emerald-600" size={20} />
            Mantenimiento del Sistema
          </h2>
          
          <div className="space-y-4">
            <div 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{ backgroundColor: '#f8fafc' }}
            >
              <div>
                <p className="font-medium" style={{ color: '#1e293b' }}>Limpiar Secciones Duplicadas</p>
                <p className="text-xs" style={{ color: '#64748b' }}>
                  Elimina secciones duplicadas del menú de navegación. 
                  Útil si ves módulos repetidos en el sidebar.
                </p>
              </div>
              <button 
                onClick={handleCleanupSections}
                disabled={cleaningSections}
                data-testid="cleanup-sections-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: cleaningSections ? '#e2e8f0' : '#ffffff',
                  border: '1px solid #10b981',
                  borderRadius: '6px',
                  color: '#059669',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: cleaningSections ? 'not-allowed' : 'pointer',
                }}
              >
                {cleaningSections ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    Limpiando...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Limpiar Menú
                  </>
                )}
              </button>
            </div>
          </div>
        </Card>

        {/* Procesos de Automatización (Navegadores) */}
        <Card className="p-6 bg-white" data-testid="browser-watchdog-card">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
            <Database className="text-indigo-600" size={20} />
            Procesos de Automatización
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Las automatizaciones (ROS, Waya, UIF) abren navegadores en segundo plano. Si alguno queda
            "colgado", consume memoria y puede hacer que la app deje de responder (ej: falla la carga de casos).
            El sistema limpia automáticamente los procesos colgados cada 10 minutos; aquí puedes forzarlo de inmediato.
          </p>

          <div className="flex items-center gap-3 mb-4 p-4 rounded-lg" style={{ backgroundColor: '#f8fafc' }}>
            {loadingBrowsers ? (
              <RefreshCw className="animate-spin text-slate-400" size={18} />
            ) : (
              <>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800" data-testid="browser-count">
                    {browserStatus ? `${browserStatus.browser_count} proceso(s) de navegador activo(s)` : 'Sin datos'}
                  </p>
                  {browserStatus && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Memoria navegadores: {browserStatus.total_browser_rss_mb} MB · RAM sistema usada: {browserStatus.system_memory?.percent_used}%
                    </p>
                  )}
                </div>
                <button
                  onClick={loadBrowserStatus}
                  data-testid="refresh-browser-status-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#475569', fontSize: '13px', cursor: 'pointer' }}
                >
                  <RefreshCw size={14} /> Actualizar
                </button>
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleCleanupBrowsers(false)}
              disabled={cleaningBrowsers}
              data-testid="cleanup-stale-browsers-btn"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 16px', backgroundColor: cleaningBrowsers ? '#e2e8f0' : '#fff', border: '1px solid #6366f1', borderRadius: '6px', color: '#4f46e5', fontSize: '14px', fontWeight: '500', cursor: cleaningBrowsers ? 'not-allowed' : 'pointer', flex: 1 }}
            >
              <RefreshCw size={16} className={cleaningBrowsers ? 'animate-spin' : ''} />
              Limpiar procesos colgados
            </button>
            <button
              onClick={() => handleCleanupBrowsers(true)}
              disabled={cleaningBrowsers}
              data-testid="cleanup-all-browsers-btn"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 16px', backgroundColor: cleaningBrowsers ? '#e2e8f0' : '#fff', border: '1px solid #fca5a5', borderRadius: '6px', color: '#dc2626', fontSize: '14px', fontWeight: '500', cursor: cleaningBrowsers ? 'not-allowed' : 'pointer', flex: 1 }}
            >
              <Trash2 size={16} />
              Forzar limpieza total
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-3 flex items-start gap-1.5">
            <AlertTriangle size={13} style={{ marginTop: '1px', flexShrink: 0 }} />
            "Forzar limpieza total" cierra incluso navegadores en uso. Úsalo solo si la app está colgada y no hay automatizaciones corriendo.
          </p>
        </Card>

        {/* Limpieza de Datos */}
        <Card className="p-6 bg-white" style={{ borderColor: '#fecaca' }}>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#b91c1c' }}>
            <Database size={20} style={{ color: '#dc2626' }} />
            Limpieza de Datos de Prueba
          </h2>
          
          <div 
            className="rounded-lg p-4 mb-6"
            style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} style={{ color: '#dc2626', marginTop: '2px' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#991b1b' }}>Zona de Peligro</p>
                <p className="text-xs mt-1" style={{ color: '#b91c1c' }}>
                  Las siguientes acciones eliminarán datos permanentemente. 
                  Úsalas solo cuando estés listo para pasar a producción.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{ backgroundColor: '#f8fafc' }}
            >
              <div>
                <p className="font-medium" style={{ color: '#1e293b' }}>Limpiar Alertas</p>
                <p className="text-xs" style={{ color: '#64748b' }}>Elimina todas las alertas del sistema</p>
              </div>
              <button 
                onClick={() => setShowClearAlertsDialog(true)}
                data-testid="clear-alerts-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={16} />
                Limpiar
              </button>
            </div>

            <div 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{ backgroundColor: '#f8fafc' }}
            >
              <div>
                <p className="font-medium" style={{ color: '#1e293b' }}>Limpiar Casos</p>
                <p className="text-xs" style={{ color: '#64748b' }}>Elimina todos los casos de investigación</p>
              </div>
              <button 
                onClick={() => setShowClearCasesDialog(true)}
                data-testid="clear-cases-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={16} />
                Limpiar
              </button>
            </div>

            <div 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
            >
              <div>
                <p className="font-medium flex items-center gap-2" style={{ color: '#991b1b' }}>
                  <Rocket size={16} />
                  Limpiar TODO para Producción
                </p>
                <p className="text-xs" style={{ color: '#dc2626' }}>
                  Elimina alertas y casos - Ideal para ir a LIVE
                </p>
              </div>
              <button 
                onClick={() => setShowClearAllDialog(true)}
                data-testid="clear-all-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={16} />
                Limpiar Todo
              </button>
            </div>
          </div>
        </Card>

        {/* Configuración de Tipos de Documento para Empresas */}
        <Card className="p-6 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#1e293b' }}>
            <Building2 size={20} style={{ color: '#7c3aed' }} />
            Configuración de Empresas
          </h2>
          <p className="text-sm mb-4" style={{ color: '#64748b' }}>
            Gestiona los tipos de documento requeridos para el onboarding de empresas. 
            Los documentos obligatorios aparecerán en rojo si faltan en el perfil de la empresa.
          </p>
          
          <TiposDocumentoConfig />
        </Card>

        {/* Configuración de Tipos de Caso */}
        <Card className="p-6 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{ color: '#1e293b' }}>
            <Briefcase size={20} style={{ color: '#3b82f6' }} />
            Configuración de Casos
          </h2>
          <p className="text-sm mb-4" style={{ color: '#64748b' }}>
            Gestiona los tipos de caso disponibles. Puedes personalizar colores e iconos para identificar rápidamente cada tipo.
          </p>
          
          <TiposCasoConfig />
        </Card>

      </div>

      {/* Alert Dialog - Clear Alerts */}
      <AlertDialog open={showClearAlertsDialog} onOpenChange={setShowClearAlertsDialog}>
        <AlertDialogContent style={{ backgroundColor: '#ffffff' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ color: '#b91c1c' }}>
              <AlertTriangle size={20} />
              ¿Eliminar todas las alertas?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente todas las alertas del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingData}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearAlerts}
              disabled={clearingData}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {clearingData ? 'Eliminando...' : 'Sí, eliminar alertas'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert Dialog - Clear Cases */}
      <AlertDialog open={showClearCasesDialog} onOpenChange={setShowClearCasesDialog}>
        <AlertDialogContent style={{ backgroundColor: '#ffffff' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ color: '#b91c1c' }}>
              <AlertTriangle size={20} />
              ¿Eliminar todos los casos?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente todos los casos de investigación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingData}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearCases}
              disabled={clearingData}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {clearingData ? 'Eliminando...' : 'Sí, eliminar casos'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert Dialog - Clear All */}
      <AlertDialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
        <AlertDialogContent style={{ backgroundColor: '#ffffff' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ color: '#b91c1c' }}>
              <AlertTriangle size={20} />
              ¿Limpiar TODOS los datos de prueba?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente:
              <ul style={{ listStyleType: 'disc', marginLeft: '24px', marginTop: '8px' }}>
                <li>Todas las alertas</li>
                <li>Todos los casos de investigación</li>
              </ul>
              <p style={{ marginTop: '12px', fontWeight: '500' }}>Esta acción NO se puede deshacer.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingData}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearAll}
              disabled={clearingData}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {clearingData ? 'Eliminando...' : 'Sí, limpiar todo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SystemSettings;
